import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { userService } from '../../../services/userService';

const UserEditModal = ({ user, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    role: 'member',
    membership_tier: 'free',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user?.full_name || '',
        email: user?.email || '',
        role: user?.role || 'member',
        membership_tier: user?.membership_tier || 'free',
        is_active: user?.is_active !== undefined ? user?.is_active : true
      });
    }
  }, [user]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.full_name?.trim()) {
      newErrors.full_name = 'Full name is required';
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      const updatedUser = await userService?.updateUser(user?.id, formData);
      onSave(updatedUser);
    } catch (error) {
      console.error('Error updating user:', error);
      setErrors({ submit: 'Failed to update user. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Edit User</h3>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Full Name
            </label>
            <Input
              type="text"
              value={formData?.full_name}
              onChange={(e) => handleInputChange('full_name', e?.target?.value)}
              error={errors?.full_name}
              placeholder="Enter full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Email
            </label>
            <Input
              type="email"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              placeholder="Enter email address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Role
            </label>
            <Select
              value={formData?.role}
              onChange={(value) => handleInputChange('role', value)}
              options={[
                { value: 'member', label: 'Member' },
                { value: 'premium_member', label: 'Premium Member' },
                { value: 'admin', label: 'Administrator' }
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Membership Tier
            </label>
            <Select
              value={formData?.membership_tier}
              onChange={(value) => handleInputChange('membership_tier', value)}
              options={[
                { value: 'free', label: 'Free' },
                { value: 'premium', label: 'Premium' },
                { value: 'gold', label: 'Gold' }
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Status
            </label>
            <Select
              value={formData?.is_active ? 'active' : 'inactive'}
              onChange={(value) => handleInputChange('is_active', value === 'active')}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
            />
          </div>

          {errors?.submit && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{errors?.submit}</p>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserEditModal;